import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toSlug, buildSlug } from '../../utils/slug';
import { imageFallback } from '../../utils/env';
import { storeColor } from '../../constants/stores';

function stripMarkdown(text) {
  return text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .trim();
}

function truncate(text, maxLength = 80) {
  if (!text) return '';
  const plain = stripMarkdown(text);
  return plain.length > maxLength ? plain.substring(0, maxLength) + '...' : plain;
}

// Prices arrive from the API as strings and sometimes carry a currency symbol
// or separators, so parse defensively before doing arithmetic.
function toAmount(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : null;
}

// Absolute rupees saved reads better on a card than a second copy of the
// percentage the discount badge already shows.
function savedAmount(actualPrice, currentPrice) {
  const actual = toAmount(actualPrice);
  const current = toAmount(currentPrice);
  if (actual === null || current === null) return null;
  const diff = Math.round(actual - current);
  return diff > 0 ? diff : null;
}

// Recency is the strongest trust signal on a deals site, so surface it even
// when a deal is months old rather than quietly hiding the age.
function timeAgo(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 0) return null;
  if (minutes < 60) return minutes <= 1 ? 'Just now' : `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return null;
}

// Hex + alpha suffix rather than color-mix(), which isn't in every browser
// this project's browserslist targets. Same pattern as the store cards.
function storePillStyle(store) {
  const color = storeColor(store);
  return { '--store-color': color, '--store-tint': `${color}1a` };
}

function HeartIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20"
      fill={filled ? '#ef4444' : 'none'}
      stroke={filled ? '#ef4444' : '#475569'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function OfferCard({ item, savedIds, onToggleSave }) {
  const navigate = useNavigate();
  const saved = savedIds ? savedIds.has(item.id) : false;
  const validDiscount = Number(item.offerPercentage) > 0;
  const savings = savedAmount(item.actualPrice, item.currentPrice);
  const posted = timeAgo(item.createdAt);
  const couponCode = item.couponCode;
  // Real, crawlable URL so Googlebot can discover & follow to each offer page.
  const href = `/offers/${toSlug(item.type)}/${buildSlug(item.title, item.id)}`;

  function handleClick(e) {
    // Let the browser handle new-tab / modifier clicks natively (real link).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    navigate(href, { state: { offer: item } });
  }

  return (
    <a
      href={href}
      className="card"
      onClick={handleClick}
      style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      <div className="card-image-wrap">
        <img
          src={item.image || '/default-offer.svg'}
          alt={item.store ? `${item.title} — ${item.store} offer on OfferLelo` : `${item.title} offer on OfferLelo`}
          className="card-image"
          loading="lazy"
          decoding="async"
          width="400"
          height="300"
          onError={imageFallback('/default-offer.svg')}
        />
        {validDiscount && (
          <span className="card-discount-badge">{item.offerPercentage}% off</span>
        )}
        {couponCode && (
          <span className="card-coupon-badge">🎟 Coupon Available</span>
        )}
        <button
          type="button"
          className={`card-save-btn ${saved ? 'saved' : ''}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave && onToggleSave(item); }}
          aria-label={saved ? 'Remove from saved' : 'Save offer'}
        >
          <HeartIcon filled={saved} />
        </button>
      </div>
      <div className="card-content">
        <h3 className="card-title">{item.title}</h3>
        {(item.currentPrice || item.actualPrice) && (
          <div className="card-price-row">
            {item.currentPrice && <span className="card-price-current">₹{item.currentPrice}</span>}
            {item.actualPrice && <del className="card-price-mrp">₹{item.actualPrice}</del>}
            {savings !== null && (
              <span className="card-price-save">Save ₹{savings.toLocaleString('en-IN')}</span>
            )}
          </div>
        )}
        {(item.store || posted) && (
          <div className="card-meta-row">
            {item.store && (
              <span className="card-store-pill" style={storePillStyle(item.store)}>
                <span className="card-store-pill-mark" aria-hidden="true">
                  {item.store.trim().charAt(0).toUpperCase()}
                </span>
                <span className="card-store-pill-name">{item.store}</span>
              </span>
            )}
            {posted && (
              <time className="card-posted" dateTime={item.createdAt}>{posted}</time>
            )}
          </div>
        )}
        {item.description && (
          <p className="card-description">{truncate(item.description, 80)}</p>
        )}
        <span className="card-link">View Deal →</span>
      </div>
    </a>
  );
}

// Memoize so cards don't re-render on unrelated parent state changes (search typing,
// trending banner auto-rotate, etc.). Custom comparator: only re-render when this
// specific item's saved-state flips, or item/handler reference changes.
export default memo(OfferCard, (prev, next) =>
  prev.item === next.item &&
  prev.onToggleSave === next.onToggleSave &&
  Boolean(prev.savedIds?.has(prev.item.id)) === Boolean(next.savedIds?.has(next.item.id))
);
